import React from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Grid, LinearProgress
} from '@mui/material'
import { AppAnalyticsCard, AppCardTitle } from "@components"
import { dsColorNew, dataColors } from "@palette"


// Same donut + side legend as the page's PieAmchartSeeker (innerRadius 60, vertical legend)
function PieChartPlaceholder({ data, colors }) {
	const total = data.reduce((s, d) => s + d.value, 0)
	return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
		<Box sx={{
			width: 140, height: 140, borderRadius: '50%', position: 'relative', flexShrink: 0,
			background: `conic-gradient(${data.map((d, i) => {
				const start = data.slice(0, i).reduce((s, x) => s + x.value, 0) / total * 360
				const end = start + (d.value / total * 360)
				return `${colors[i % colors.length]} ${start}deg ${end}deg`
			}).join(', ')})`,
			'&::after': { content: '""', position: 'absolute', inset: 30, borderRadius: '50%', backgroundColor: dsColorNew.white },
		}} />
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
			{data.map((d, i) => (
				<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: colors[i % colors.length] }} />
					<Typography sx={{ fontSize: 12, color: dsColorNew.textDark, maxWidth: 140, overflowWrap: 'anywhere' }}>
						{d.label}: <strong>{d.value.toLocaleString()}</strong>
					</Typography>
				</Box>
			))}
		</Box>
	</Box>
}

// The page's two series: "Searches" in blue and "Searches Previous Month" in light cyan
function BarChartPlaceholder() {
	const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
	const current = [185, 210, 195, 230, 245, 120, 95]
	const previous = [160, 180, 175, 200, 220, 110, 85]
	const max = Math.max(...current, ...previous)

	return <Card sx={{ height: '100%' }}>
		<CardContent>
			<Typography sx={{ mb: 2, fontSize: 15, fontWeight: 600, color: dsColorNew.textTitle }}>
				Daily searches vs Previous Month
			</Typography>
			<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 200, px: 1, borderBottom: `1px solid ${dsColorNew.grayBorder}` }}>
				{days.map((day, i) => (
					<Box key={i} sx={{ flex: 1, display: 'flex', gap: 0.3, alignItems: 'flex-end', justifyContent: 'center' }}>
						<Box sx={{
							width: '40%', height: (current[i] / max) * 180,
							bgcolor: dsColorNew.appBlue, borderRadius: '3px 3px 0 0',
						}} />
						<Box sx={{
							width: '40%', height: (previous[i] / max) * 180,
							bgcolor: dataColors.cyanSoft, borderRadius: '3px 3px 0 0',
						}} />
					</Box>
				))}
			</Box>
			<Box sx={{ display: 'flex', gap: 1.5, px: 1, mt: 0.5 }}>
				{days.map(day => <Typography key={day} sx={{ flex: 1, textAlign: 'center', fontSize: 11, color: dsColorNew.textMuted }}>{day}</Typography>)}
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1.5 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
					<Box sx={{ width: 12, height: 12, bgcolor: dsColorNew.appBlue, borderRadius: '3px' }} />
					<Typography sx={{ fontSize: 12, color: dsColorNew.textDark }}>Searches</Typography>
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
					<Box sx={{ width: 12, height: 12, bgcolor: dataColors.cyanSoft, borderRadius: '3px' }} />
					<Typography sx={{ fontSize: 12, color: dsColorNew.textDark }}>Searches Previous Month</Typography>
				</Box>
			</Box>
		</CardContent>
	</Card>
}

function InfoBlockDemo({ title, data, colors, tourId }) {
	const max = Math.max(...data.map(d => d.value))
	return <Box data-tour-id={tourId} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
		<AppCardTitle icon="chart-pie" title={title} sx={{ alignSelf: 'flex-start' }}/>
		<PieChartPlaceholder data={data} colors={colors} />
		<Table size="small">
			<TableHead>
				<TableRow>
					<TableCell>{title.replace('Searches by ', '')}</TableCell>
					<TableCell align="center">Searches</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{data.map((d, i) => (
					<TableRow key={i} sx={{
						backgroundColor: i % 2 === 0 ? dsColorNew.white : dsColorNew.tableRowAlt,
						'& td': { fontSize: 13, py: 1 },
					}}>
						<TableCell>{d.label}</TableCell>
						<TableCell align="center">
							{/* the page's Searches cell: a bar for the share and the count in bold */}
							<Box sx={{ display: 'inline-flex', alignItems: 'center', width: '60%' }}>
								<Box sx={{ width: '70%', mr: 1 }}>
									<LinearProgress variant="determinate" value={d.value / max * 100}/>
								</Box>
								<Typography sx={{ fontSize: 13, fontWeight: 700, color: dsColorNew.textDark }}>{d.value.toLocaleString()}</Typography>
							</Box>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	</Box>
}


const COMPANY_DATA = [
	{ label: 'DrivePro Iberia', value: 487 },
	{ label: 'DrivePro UK', value: 312 },
	{ label: 'DrivePro France', value: 245 },
	{ label: 'DrivePro Germany', value: 189 },
	{ label: 'DrivePro Italy', value: 147 },
]

const USER_DATA = [
	{ label: 'john.smith@drivepro.demo', value: 342 },
	{ label: 'maria.garcia@drivepro.demo', value: 289 },
	{ label: 'pierre.dupont@drivepro.demo', value: 234 },
	{ label: 'anna.mueller@drivepro.demo', value: 178 },
	{ label: 'luca.rossi@drivepro.demo', value: 145 },
]

const SITE_DATA = [
	{ label: 'rentalspot.demo', value: 410 },
	{ label: 'pricefly.demo', value: 298 },
	{ label: 'eurodrive.demo', value: 256 },
	{ label: 'bookride.demo', value: 187 },
	{ label: 'autolink.demo', value: 129 },
]

const PIE_COLORS_1 = [dsColorNew.webBlue500, dsColorNew.appBlue, dataColors.sky500, dataColors.cyanSoft, dataColors.slate200]
const PIE_COLORS_2 = [dataColors.green800, dataColors.green600, dataColors.emerald500, dataColors.green300, dataColors.green100]
const PIE_COLORS_3 = [dataColors.violet700, dataColors.violet500, dataColors.pink500, dataColors.orange300, dataColors.red100]


// Same grid as the page's Content: KPI cards stacked on the left, the chart beside them, three info blocks below
export default function AnalyticsDemoData() {
	return <Grid container spacing={2}>
		<Grid item xs={2}>
			<Box data-tour-id="analytics-kpi-cards" sx={{ display: 'flex', alignItems: 'stretch', height: '100%', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
				<AppAnalyticsCard countUp={1380} label="Date Range" icon="calendar-range" iconColor="colors.green" />
				<AppAnalyticsCard countUp={312} label="This week" icon="calendar-week" iconColor="colors.blue" />
				<AppAnalyticsCard countUp={1890} label="This month" icon="calendar-days" iconColor="colors.red" />
			</Box>
		</Grid>

		<Grid item xs={10}>
			<BarChartPlaceholder />
		</Grid>

		<Grid item xs={4}>
			<InfoBlockDemo title="Searches by Company" data={COMPANY_DATA} colors={PIE_COLORS_1} tourId="analytics-searches-by-company" />
		</Grid>
		<Grid item xs={4}>
			<InfoBlockDemo title="Searches by User" data={USER_DATA} colors={PIE_COLORS_2} tourId="analytics-searches-by-user" />
		</Grid>
		<Grid item xs={4}>
			<InfoBlockDemo title="Searches by Site" data={SITE_DATA} colors={PIE_COLORS_3} tourId="analytics-searches-by-site" />
		</Grid>
	</Grid>
}
